import axios from "axios";
import { toast } from "react-hot-toast";

// always send cookies with XHR/fetch (required for session auth across ports)
axios.defaults.withCredentials = true;

// this api will be forced when deployed
const API_URL_PROD = "https://betterblocks.landmarkventures.com:7770/api/";
const API_URL_PROD_BOTS = "https://landmarkbots.com:7770/api/";

// here you can choose which API to run locally (when devving)
const API_URL = "https://localhost:7770/api/";
//const API_URL = API_URL_PROD;

// here you can choose which BOTS API to use (when devving locally) -
// bots endpoints normally always hit the prod bots server, flip the comments
// to test bots endpoints against a locally running blocks-backend
const API_URL_BOTS = API_URL_PROD_BOTS;
//const API_URL_BOTS = "https://localhost:7770/api/";
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
const SENDORA_MIN_REQUEST_TIMEOUT_MS = 60000;
const SENDORA_ENDPOINT_PREFIX = "landmark/sendora";
const DYNAMIC_EVENT_AI_MIN_REQUEST_TIMEOUT_MS = 60000;
const DYNAMIC_EVENT_AI_ENDPOINT_PREFIX = "landmark/dynamicevents/ai";
// translating a whole event page is a long single model call
const DYNAMIC_EVENT_TRANSLATE_MIN_REQUEST_TIMEOUT_MS = 180000;
const DYNAMIC_EVENT_TRANSLATE_ENDPOINT = "landmark/dynamicevents/ai/translateEvent";
// ceo sniper endpoints can be slow - the bots server is often busy running
// puppeteer scrapes and getAllTasks/createBatchJob move a lot of data
const CEO_SNIPER_MIN_REQUEST_TIMEOUT_MS = 60000;
const CEO_SNIPER_ENDPOINT_PREFIX = "landmark/ceosniper";

function getAuthToken() {
  return localStorage.getItem("token");
}

function resolveRequestTimeout(endpoint, requestedTimeout) {
  const normalizedEndpoint = String(endpoint || "").replace(/^\/+/, "");
  const isSendoraEndpoint =
    normalizedEndpoint === SENDORA_ENDPOINT_PREFIX ||
    normalizedEndpoint.startsWith(`${SENDORA_ENDPOINT_PREFIX}/`);
  const isDynamicEventAiEndpoint =
    normalizedEndpoint === DYNAMIC_EVENT_AI_ENDPOINT_PREFIX ||
    normalizedEndpoint.startsWith(`${DYNAMIC_EVENT_AI_ENDPOINT_PREFIX}/`);
  const isCeoSniperEndpoint =
    normalizedEndpoint === CEO_SNIPER_ENDPOINT_PREFIX ||
    normalizedEndpoint.startsWith(`${CEO_SNIPER_ENDPOINT_PREFIX}/`);
  if (!isSendoraEndpoint && !isDynamicEventAiEndpoint && !isCeoSniperEndpoint) {
    return requestedTimeout;
  }

  let timeoutFloor = 0;
  if (isSendoraEndpoint) {
    timeoutFloor = Math.max(timeoutFloor, SENDORA_MIN_REQUEST_TIMEOUT_MS);
  }
  if (isDynamicEventAiEndpoint) {
    timeoutFloor = Math.max(
      timeoutFloor,
      normalizedEndpoint === DYNAMIC_EVENT_TRANSLATE_ENDPOINT
        ? DYNAMIC_EVENT_TRANSLATE_MIN_REQUEST_TIMEOUT_MS
        : DYNAMIC_EVENT_AI_MIN_REQUEST_TIMEOUT_MS
    );
  }
  if (isCeoSniperEndpoint) {
    timeoutFloor = Math.max(timeoutFloor, CEO_SNIPER_MIN_REQUEST_TIMEOUT_MS);
  }

  const timeoutNumber = Number(requestedTimeout);
  if (Number.isFinite(timeoutNumber)) {
    // Keep explicit no-timeout and any timeout already longer than the floor.
    if (timeoutNumber === 0) return 0;
    return Math.max(timeoutNumber, timeoutFloor);
  }

  return timeoutFloor;
}

class ApiService {
  constructor(options = {}) {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    this.apiUrl = isLocalhost ? API_URL : API_URL_PROD; // force prod url when not running locally
    if (options.apiUrl) {
      this.apiUrl = options.apiUrl;
    } else if (options.botsApiUrl) {
      // force prod bots url when not running locally
      this.apiUrl = isLocalhost ? API_URL_BOTS : API_URL_PROD_BOTS;
    }
    this.apiUrlRoot = this.apiUrl.replace("/api/", "/");
    this.token = getAuthToken();
    this.password = options.password ? options.password : "";
    this.employeeId = options.employee ? options.employee.employeeId : "";

    // dedicated axios instance
    this.http = axios.create({
      withCredentials: true, // ensure cookies are included
      timeout: DEFAULT_REQUEST_TIMEOUT_MS,
    });
  }

  // Build headers; include Authorization only if we have a real token.
  // Also skip Authorization on auth endpoints to avoid interfering with cookie session auth.
  buildHeaders(endpoint, extra = {}) {
    const headers = { ...extra };

// Always read the freshest token
    const token = getAuthToken();
    const hasBearer = token && token !== "null" && token !== "undefined";
    // We can skip Bearer ONLY for endpoints that should never need it (like sendLoginLink or verifyMagicLink).
    const skipBearer =
      typeof endpoint === "string" &&
      (endpoint.startsWith("landmark/employees/auth/sendLoginLink") ||
       endpoint.startsWith("landmark/employees/auth/verifyMagicLink"));


    if (!skipBearer && hasBearer) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (this.password) headers.password = `${this.password}`;
    if (this.employeeId) headers.employeeId = `${this.employeeId}`;

    return headers;
  }

    /**
   * Build an absolute SSE URL from an API-relative path.
   * Example: buildSseUrl(`/sse/stream/${jobId}`)
   */
  buildSseUrl(ssePath) {
    // this.apiUrl ends with `/api/`
    const base = this.apiUrl.replace(/\/+$/, ""); // strip trailing slash
    const path = String(ssePath || "").replace(/^\/+/, ""); // strip leading slash
    return `${base}/${path}`;
  }

  /**
   * Open an SSE stream with credentials and attach listeners.
   * Returns a controller with `close()` and the raw `eventSource`.
   *
   * @param {Object} options
   * @param {string} options.path - API path relative to /api/, e.g. "sse/stream/<jobId>"
   * @param {function} [options.onOpen]
   * @param {function} [options.onLog]
   * @param {function} [options.onDone]
   * @param {function} [options.onError] - note: also receives generic EventSource errors (disconnects, etc.)
   * @param {Object<string,function>} [options.events] - map of custom event names -> handler
   * @returns {{ close: Function, eventSource: EventSource, url: string }}
   */
  openEventStream(options = {}) {
    const {
      path,
      onOpen,
      onLog,
      onDone,
      onError,
      events = {},
    } = options;

    if (!path) {
      throw new Error("openEventStream: `path` is required, e.g. 'sse/stream/<jobId>'");
    }

    const url = this.buildSseUrl(path);

    // IMPORTANT: credentials so cookies/session are sent cross-origin
    const es = new EventSource(url, { withCredentials: true });

    // Attach common event handlers
    if (typeof onOpen === "function") {
      es.onopen = (ev) => onOpen(ev, { url, es });
    }

    // Server emits "log", "done", "error" events
    if (typeof onLog === "function") {
      es.addEventListener("log", (ev) => {
        let data = ev.data;
        try { data = JSON.parse(ev.data); } catch {}
        onLog(data, ev);
      });
    }

    if (typeof onDone === "function") {
      es.addEventListener("done", (ev) => {
        let data = ev.data;
        try { data = JSON.parse(ev.data); } catch {}
        onDone(data, ev);
        es.close();
      });
    }

    if (typeof onError === "function") {
      // Note: fires on disconnects & network/CORS issues; may not include data
      es.addEventListener("error", (ev) => {
        let data = undefined;
        try { if (ev.data) data = JSON.parse(ev.data); } catch { data = ev.data; }
        onError(data, ev);
      });
    }

    // Custom events map: { "myEvent": handlerFn, ... }
    Object.entries(events).forEach(([evt, handler]) => {
      if (typeof handler === "function") {
        es.addEventListener(evt, (ev) => {
          let data = ev.data;
          try { data = JSON.parse(ev.data); } catch {}
          handler(data, ev);
        });
      }
    });

    return {
      url,
      eventSource: es,
      close: () => es.close(),
    };
  }

  /**
   * Convenience wrapper specifically for your job streams.
   * @param {string} jobId
   * @param {Object} handlers - same handlers as openEventStream
   */
  openJobStream(jobId, handlers = {}) {
    if (!jobId) throw new Error("openJobStream: jobId is required");
    // server route: /api/sse/stream/:jobId  => path should be "sse/stream/<jobId>"
    const path = `sse/stream/${jobId}`;
    return this.openEventStream({ path, ...handlers });
  }


  async ping() {
    try {
      await this.http.get(`${this.apiUrl}misc/ping`, { withCredentials: true });
      return true;
    } catch (error) {
      return false;
    }
  }

  async get(endpoint, params, responseType, config = {}) {
  try {
    const options = {
      withCredentials: true,
      headers: { ...this.buildHeaders(endpoint), ...(config.headers || {}) },
      ...config,
    };
    const resolvedTimeout = resolveRequestTimeout(endpoint, options.timeout);
    if (typeof resolvedTimeout === "number") {
      options.timeout = resolvedTimeout;
    }
    if (params && Object.keys(params).length) options.params = params;
    if (responseType) options.responseType = responseType;

    const res = await this.http.get(`${this.apiUrl}${endpoint}`, options);
    return res.data;
  }
  catch (error) {
    if (error.response) {
      console.log(error.response.status);
      console.log(error.response.data);
      return error.response.data || { error: "Server error" };
    } else if (error.request) {
      console.log(error.request);
      return {
        error:
          error?.code === "ECONNABORTED"
            ? "Request timed out"
            : "No response from server",
      };
    } else {
      console.log("Error", error.message);
      return { error: error.message || "Request failed" };
    }
  }
}

  async post(endpoint, data, responseType, config = {}) {
    return this.send("post", endpoint, data, responseType, config);
  }

  async put(endpoint, data, responseType, config = {}) {
    return this.send("put", endpoint, data, responseType, config);
  }

  async delete(endpoint, config = {}) {
    return this.send("delete", endpoint, undefined, undefined, config);
  }

  // shared implementation for post / put / delete
  async send(method, endpoint, data, responseType, config = {}) {
    try {
      const options = {
        withCredentials: true,
        headers: this.buildHeaders(endpoint),
        ...config, // allow caller to override timeout/responseType/etc.
      };
      const resolvedTimeout = resolveRequestTimeout(endpoint, options.timeout);
      if (typeof resolvedTimeout === "number") {
        options.timeout = resolvedTimeout;
      }
      if (responseType) {
        options.responseType = responseType;
      }
      const url = `${this.apiUrl}${endpoint}`;
      const response =
        method === "delete"
          ? await this.http.delete(url, options)
          : await this.http[method](url, data, options);
      return response.data;
    } catch (error) {
      if (error.response) {
        console.log(error.response.status);
        console.log(error.response.data);
        return error.response.data || { error: "Server error" };
      } else if (error.request) {
        console.log(error.request);
        return {
          error:
            error?.code === "ECONNABORTED"
              ? "Request timed out"
              : "No response from server",
        };
      } else {
        console.log("Error", error.message);
        return { error: error.message || "Request failed" };
      }
    }
  }
}

export default ApiService;
